import { useTranslation } from 'react-i18next';
import { Link, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ZipContentSelect } from '@/components/patient/ZipContentSelect';
import {
  XML_ATTACHMENT_ACCEPT,
  XML_ATTACHMENT_EXTENSIONS,
  needsZipObjectType,
  referralReferencesForRequest,
  type FileAttachmentDraft,
  type ZipObjectType,
  type ReferralFileLimitCode,
  type ReferralReferenceIssue,
  type UrlAttachmentDraft,
} from '@/lib/referral/referralAttachments';
import { MAX_REFERENCES } from '@/lib/referral/xref01Validation';
import { FORM_ERROR_TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export type UrlAttachmentRow = UrlAttachmentDraft & { key: string };

export type FileAttachmentRow = FileAttachmentDraft & {
  key: string;
  file: File | null;
  exchangeId: string | null;
  error: string;
};

export type AttachmentRow = UrlAttachmentRow | FileAttachmentRow;

const IMMEDIATE_FILE_CODES = new Set<ReferralFileLimitCode>([
  'unsupported-type',
  'file-too-large',
  'too-many-files',
  'total-too-large',
]);

export function HospitalReferralAttachments({
  rows,
  attempted,
  onChangeUrl,
  onChangeFileDescription,
  onChooseFile,
  onChangeZipObjectType,
  onAddUrl,
  onAddFile,
  onRemove,
}: {
  rows: readonly AttachmentRow[];
  attempted: boolean;
  onChangeUrl: (key: string, patch: Partial<Pick<UrlAttachmentDraft, 'description' | 'url'>>) => void;
  onChangeFileDescription: (key: string, description: string) => void;
  onChooseFile: (key: string, file: File) => void;
  /** C11: content type of a zip file (BIN01-S 3.3). */
  onChangeZipObjectType: (key: string, value: ZipObjectType | undefined) => void;
  onAddUrl: () => void;
  onAddFile: () => void;
  onRemove: (key: string) => void;
}) {
  const { t } = useTranslation();
  const checked = referralReferencesForRequest(
    rows.map((row) => (row.kind === 'url' ? { kind: 'url', description: row.description, url: row.url } : row)),
    { requireChecked: false },
  );
  const issues = checked.ok ? [] : checked.issues;
  const budgetFull = rows.length >= MAX_REFERENCES;

  return (
    <div className="space-y-4">
      {rows.map((row, index) => {
        const rowIssues = issues.filter((issue) => 'index' in issue && issue.index === index);
        return (
          <div
            key={row.key}
            className="space-y-3 rounded-md border border-ds-line p-3"
          >
            {row.kind === 'url' ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  id={`referral-attachment-description-${row.key}`}
                  label={t('patients.hospital_referral.attachment_description', 'Beskrivelse')}
                  value={row.description}
                  autoComplete="off"
                  onChange={(event) => onChangeUrl(row.key, { description: event.target.value })}
                />
                <Input
                  id={`referral-attachment-url-${row.key}`}
                  label={t('patients.hospital_referral.attachment_url', 'Webadresse')}
                  value={row.url}
                  autoComplete="off"
                  inputMode="url"
                  onChange={(event) => onChangeUrl(row.key, { url: event.target.value })}
                />
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  id={`referral-attachment-description-${row.key}`}
                  label={t('patients.hospital_referral.attachment_description', 'Beskrivelse')}
                  value={row.description}
                  autoComplete="off"
                  onChange={(event) => onChangeFileDescription(row.key, event.target.value)}
                />
                <Input
                  id={`referral-attachment-file-${row.key}`}
                  type="file"
                  accept={XML_ATTACHMENT_ACCEPT}
                  label={t('patients.hospital_referral.attachment_file', 'Fil')}
                  hint={row.fileName || undefined}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) onChooseFile(row.key, file);
                  }}
                />
                {needsZipObjectType(row.fileName) && (
                  <ZipContentSelect
                    id={`referral-attachment-zip-${row.key}`}
                    value={row.zipObjectType}
                    onChange={(value) => onChangeZipObjectType(row.key, value)}
                  />
                )}
              </div>
            )}
            {rowIssues
              .filter(
                (issue) =>
                  attempted ||
                  (issue.source === 'file' && IMMEDIATE_FILE_CODES.has(issue.code as ReferralFileLimitCode)),
              )
              .map((issue) => (
                <p key={issueKey(issue)} role="alert" className={FORM_ERROR_TYPE}>
                  {attachmentIssueText(t, index, issue)}
                </p>
              ))}
            {row.kind === 'file' && row.error && (
              <p role="alert" className={FORM_ERROR_TYPE}>
                {row.error}
              </p>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(row.key)}>
              {t('patients.hospital_referral.attachment_remove', 'Fjern bilag')}
            </Button>
          </div>
        );
      })}
      {!budgetFull && (
        <div className="flex flex-wrap gap-3">
          <Button type="button" variant="secondary" size="sm" onClick={onAddUrl}>
            <Link aria-hidden />
            {t('patients.hospital_referral.attachment_add', 'Tilføj webadresse')}
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={onAddFile}>
            <Paperclip aria-hidden />
            {t('patients.hospital_referral.attachment_add_file', 'Tilføj fil')}
          </Button>
        </div>
      )}
      {attempted && issues.some((issue) => issue.code === 'too_many') && (
        <p role="alert" className={cn(FORM_ERROR_TYPE)}>
          {t('patients.hospital_referral.attachment_too_many', 'Der kan højst være 10 bilag.')}
        </p>
      )}
    </div>
  );
}

function issueKey(issue: ReferralReferenceIssue): string {
  if (issue.source === 'budget') return 'budget-too-many';
  return `${issue.source}-${issue.index}-${issue.code}`;
}

function attachmentIssueText(
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
  index: number,
  issue: ReferralReferenceIssue,
): string {
  const number = index + 1;
  switch (issue.code) {
    case 'incomplete':
      if (issue.source === 'file') {
        return t(
          'patients.hospital_referral.attachment_file_incomplete',
          'Bilag {{number}}: både beskrivelse og fil skal udfyldes.',
          { number },
        );
      }
      return t(
        'patients.hospital_referral.attachment_incomplete',
        'Bilag {{number}}: både beskrivelse og webadresse skal udfyldes.',
        { number },
      );
    case 'description_long':
      return t(
        'patients.hospital_referral.too_long',
        '{{field}} er {{length}} tegn. Grænsen er {{max}}. Værdien afkortes ikke.',
        {
          field: t('patients.hospital_referral.attachment_description', 'Beskrivelse'),
          length: issue.length,
          max: issue.max,
        },
      );
    case 'url_long':
      return t(
        'patients.hospital_referral.too_long',
        '{{field}} er {{length}} tegn. Grænsen er {{max}}. Værdien afkortes ikke.',
        {
          field: t('patients.hospital_referral.attachment_url', 'Webadresse'),
          length: issue.length,
          max: issue.max,
        },
      );
    case 'latin1':
      return t(
        'patients.hospital_referral.latin1',
        '{{field}} indeholder et tegn, der ikke kan skrives i ISO-8859-1.',
        { field: t('patients.hospital_referral.attachments_section', 'Bilag') },
      );
    case 'too_many':
      return t('patients.hospital_referral.attachment_too_many', 'Der kan højst være 10 bilag.');
    case 'unsupported-type':
      return fileLimitText(t, 'unsupported-type');
    case 'file-too-large':
      return fileLimitText(t, 'file-too-large');
    case 'too-many-files':
      return fileLimitText(t, 'too-many-files');
    case 'total-too-large':
      return fileLimitText(t, 'total-too-large');
    case 'not_ready':
      return t(
        'patients.hospital_referral.attachment_file_not_ready',
        'Bilag {{number}}: filen er ikke lagt på bilaget.',
        { number },
      );
    case 'zip_type_missing':
      return t(
        'patients.hospital_referral.attachment_zip_type_missing',
        'Bilag {{number}}: vælg, hvad ZIP-filen indeholder.',
        { number },
      );
    default: {
      const unexpected: never = issue;
      return String(unexpected);
    }
  }
}

export function fileLimitText(
  t: (key: string, fallback: string) => string,
  code: ReferralFileLimitCode,
): string {
  switch (code) {
    case 'unsupported-type': {
      const head = XML_ATTACHMENT_EXTENSIONS.slice(0, -1).join(', ');
      const last = XML_ATTACHMENT_EXTENSIONS[XML_ATTACHMENT_EXTENSIONS.length - 1];
      const types = `${head} og ${last}`;
      return t(
        'patients.hospital_referral.attachment_file_type',
        `Denne filtype kan ikke sendes. Tilladte typer: ${types}.`,
      );
    }
    case 'file-too-large':
      return t(
        'patients.hospital_referral.attachment_file_too_large',
        'En fil må højst være 100 MB.',
      );
    case 'too-many-files':
      return t(
        'patients.hospital_referral.attachment_file_too_many',
        'Der kan højst vedlægges 10 filer.',
      );
    case 'total-too-large':
      return t(
        'patients.hospital_referral.attachment_total_too_large',
        'Filerne er tilsammen over 100 MB.',
      );
    default: {
      const unexpected: never = code;
      return unexpected;
    }
  }
}
